<template>
  <div
    id="left_side_navbar"
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <b-img
                  height="40"
                  :src="appLogoIcon"
                  alt="logo"
                  class="brand-logo-dark"
                />
              </span>
              <b-img
                height="40"
                :src="appLogoText"
                alt="logo"
                class="brand-text"
              />
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle d-none">
            <b-link
              v-b-tooltip.hover.right="'Toggle side bar'"
              class="nav-link modern-nav-toggle"
            >
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <template v-if="doneNavItems">
      <vue-perfect-scrollbar
        :settings="perfectScrollbarSettings"
        class="main-menu-content scroll-area mt-3"
        tagname="ul"
        @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
      >
        <vertical-nav-menu-items
          :items="updatedNavMenuItems"
          :is-expanded="!isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered)"
          class="navigation navigation-main"
        />
      </vue-perfect-scrollbar>
    </template>
    <!-- /main menu content-->
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg, VBTooltip } from 'bootstrap-vue'
import {
  provide, computed, ref, watch,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import connectionIcon from '@/assets/images/icons/connectionsIcon.svg'
import { constants } from '@kingpin-global/kingpin-utils-frontend'
import { parseResource } from '@/@core/utils/utils'
import { NAV_ITEM_ID, FLAG_CONSTANTS } from '@/constants'
import { GET_COMMON_AUTH } from '@/store/modules/common.module'
import store from '@/store'
import { UPDATE_DIRECT_BRANDS } from '@/store/modules/shop.module'
import _ from 'lodash'
import { Settings } from '@/Settings'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

const { BRAND_ACCESS, ROLES } = constants

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props, { root }) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
      NAV_MENU_ITEM,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const {
      appName,
      appLogoImage,
      appLogoIcon,
      appLogoText,
    } = $themeConfig.app

    const doneNavItems = ref(false)

    const updatedNavMenuItems = ref([])

    const userRole = computed(() => root.$store.getters.entityType)
    const isBrand = computed(() => root.$store.getters.isBrand)
    const isRetailer = computed(() => root.$store.getters.isRetailer)
    const isGuest = (userRole.value === ROLES.GUEST)
    const profile = computed(() => root.$store.getters.profile)
    let isBrandsAccessible = true
    setTimeout(() => {
      if (isRetailer.value) {
        isBrandsAccessible = profile.value?.access?.includes(BRAND_ACCESS.MARKETPLACE)
      }
    }, 600)
    const navMenuItems = [{
      header: 'Welcome to Kingpin',
    },
      NAV_MENU_ITEM.DASHBOARD(isGuest),
      NAV_MENU_ITEM.PROFILE(isGuest),
    ]
    // Helper method to build direct brands menu
    const buildDirectBrandsMenu = async () => {
      if (!isRetailer.value) return []

      try {
        const response = await store.dispatch(GET_COMMON_AUTH)
        const directBrands = response?.data?.data?.DIRECT_BRANDS || []
        store.commit(UPDATE_DIRECT_BRANDS, directBrands)
        return directBrands.map((brand, index) => ({
          tagId: index === 0 ? 'intro_explore' : '',
          title: brand.name,
          img: brand.logo,
          route: {
            header: brand.name,
            name: 'brand/detail',
            params: { brandId: brand._id },
          },
          ignoreActive: true,
          resource: 'Retailer',
        }))
      } catch (error) {
        return []
      }
    }
    // Helper method to build explore menu for retailers
    const buildExploreMenu = directBrandsMenu => ({
      tagId: 'intro_explore',
      title: 'Explore',
      icon: 'ShoppingBagIcon',
      navItemId: NAV_ITEM_ID.EXPLORE,
      children: [
        ...directBrandsMenu,
        {
          title: 'Premium Brands',
          route: 'premiumbrands',
          resource: 'Retailer',
          disabled: !isBrandsAccessible,
          icon: !isBrandsAccessible ? 'SlashIcon' : '',
        },
        {
          title: 'All Brands',
          route: 'explore',
          resource: parseResource('Retailer'),
          disabled: !isBrandsAccessible,
          icon: !isBrandsAccessible ? 'SlashIcon' : '',
        },
      ],
    })

    // Helper method to add retailer-specific menu items
    const addRetailerMenuItems = menuItems => {
      menuItems.push(NAV_MENU_ITEM.WISHLIST(isGuest))
      menuItems.push(NAV_MENU_ITEM.CHECKOUT(isGuest))
      return menuItems
    }

    // Helper method to add brand-specific menu items
    const addBrandMenuItems = menuItems => {
      menuItems.push(NAV_MENU_ITEM.COLLECTION)
      menuItems.push(NAV_MENU_ITEM.SELECTION(isGuest))
      menuItems.push(NAV_MENU_ITEM.SALES_ANALAYTICS(isGuest))
      return menuItems
    }

    // Helper method to add common menu items
    const addCommonMenuItems = menuItems => {
      const connectionTitle = root.$store.getters.entityType === ROLES.BRAND ? 'My Retailers' : 'My Brands'
      menuItems.push(NAV_MENU_ITEM.CONNECTIONS(connectionTitle, connectionIcon, isGuest))
      menuItems.push(NAV_MENU_ITEM.ORDERS(isGuest))
      menuItems.push(NAV_MENU_ITEM.SHIPMENTS(isGuest))
      menuItems.push(NAV_MENU_ITEM.DOWNLOADS(isGuest))
      return menuItems
    }

    // Helper method to add brand-specific analytics items
    const addBrandAnalyticsItems = menuItems => {
      if (!isBrand) return menuItems

      if (profile.value?.isAnalyticsEnabled) {
        menuItems.push(NAV_MENU_ITEM.ANALYTICS)
      }
      if (profile.value?.isLeadAIEnabled) {
        menuItems.push(NAV_MENU_ITEM.LEAD_AI)
      }
      if (profile.value?.isHyperDXAnalyticsEnabled) {
        menuItems.push(NAV_MENU_ITEM.INTEGRATION_METRICS)
      }
      return menuItems
    }

    // Helper method to add help menu items
    const addHelpMenuItems = menuItems => {
      menuItems.push({
        tagId: 'intro_help',
        title: 'Help',
        icon: 'HelpCircleIcon',
        href: Settings.getConfig('VUE_APP_HELP_URL'),
        resource: 'Help',
        action: 'read',
        navItemId: NAV_ITEM_ID.HELP,
      })
      menuItems.push({
        tagId: 'intro_knowledge_base',
        title: 'Knowledge Center',
        icon: 'BookOpenIcon',
        route: 'knowledge-center',
        resource: 'Help',
        action: 'read',
        navItemId: NAV_ITEM_ID.KNOWLEDGE_CENTER,
      })
      menuItems.push({
        tagId: 'intro_kingpin_academy',
        title: 'Kingpin Academy',
        icon: 'BookIcon',
        route: 'kingpin-academy',
        resource: 'Help',
        action: 'read',
        navItemId: NAV_ITEM_ID.KINGPIN_ACADEMY,
      })
      return menuItems
    }

    const buildRealNavMenu = async () => {
      const realNavMenuItems = ref([])
      realNavMenuItems.value = JSON.parse(JSON.stringify(navMenuItems)) || []

      // Handle retailer-specific navigation
      if (userRole.value === ROLES.RETAILER || isGuest) {
        addRetailerMenuItems(realNavMenuItems.value)

        const directBrandsMenu = await buildDirectBrandsMenu()
        const exploreMenu = buildExploreMenu(directBrandsMenu)
        realNavMenuItems.value = [
          ...JSON.parse(JSON.stringify(navMenuItems)),
          exploreMenu,
          ...realNavMenuItems.value.splice(navMenuItems.length),
        ]
        doneNavItems.value = true
      } else {
        // Handle brand-specific navigation
        addBrandMenuItems(realNavMenuItems.value)
        doneNavItems.value = true
      }

      // Add common menu items
      addCommonMenuItems(realNavMenuItems.value)

      // Add brand-specific analytics items
      addBrandAnalyticsItems(realNavMenuItems.value)
      // Add help menu items
      addHelpMenuItems(realNavMenuItems.value)

      return realNavMenuItems.value
    }

    const loadNavMenues = async () => {
      updatedNavMenuItems.value = await buildRealNavMenu()
      updatedNavMenuItems.value = addRetailerAi(updatedNavMenuItems.value)
    }

    const addRetailerAi = updatedMenus => {
      if (store.state.flags[FLAG_CONSTANTS.RETAILER_AI]) {
        const downloadsIndex = updatedMenus.findIndex(item => item.title === 'Downloads'
          || (item.navItemId && item.navItemId === NAV_ITEM_ID.DOWNLOADS))

        if (downloadsIndex !== -1) {
          updatedMenus = [
            ...updatedMenus.slice(0, downloadsIndex + 1),
            NAV_MENU_ITEM.RETAILER_AI,
            ...updatedMenus.slice(downloadsIndex + 1),
          ]
        } else {
          updatedMenus.push(NAV_MENU_ITEM.RETAILER_AI)
        }
      }
      return updatedMenus
    }

    const updateNavMenuItems = async () => {
      let updatedMenues = await buildRealNavMenu()

      updatedMenues = addRetailerAi(updatedMenues)

      if (!_.isEqual(updatedMenues, updatedNavMenuItems.value)) {
        updatedNavMenuItems.value = updatedMenues
      }
    }

    loadNavMenues()
    // watch until profile is updated
    watch(profile, () => {
      updateNavMenuItems()
    })

    watch(() => store.state.flags[FLAG_CONSTANTS.RETAILER_AI], () => {
      updateNavMenuItems()
    })

    return {
      updatedNavMenuItems,
      doneNavItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
      appLogoIcon,
      appLogoText,
    }
  },
}
</script>

<style lang="scss">
@import "src/@core/scss/base/core/menu/menu-types/vertical-menu.scss";
@import 'src/@core/scss/base/bootstrap-extended/_variables.scss';

.main-menu.menu-light .navigation>li.active>a {
  background: $navbar-active-bg-color !important;
  border-radius: 5px;
  margin-bottom: 0.5em;
  margin-top: 0.5em;
  color: $body-color;
  box-shadow: unset;
}
</style>
